import React, { FC, useContext, useMemo } from "react";
import PostalAddress from "i18n-postal-address";
import { LocalizationContext } from "v3/context/localization";
import { TenantContext } from "v3/context/tenant";
import { Button } from "@pepdirect/v3/ui/Button";
import { Heading } from "@pepdirect/v3/ui/Heading";
import { TemplateAsComponent } from "@pepdirect/v3/ui/TemplateAsComponent";
import {
  ShippingAddress,
  ValidationRequirement,
} from "v3/services/graphql/generated";
import {
  FormatCityStateZipByCountry,
  formatCityStateZipByCountry,
  formatStreetByCountry,
} from "@pepdirect/v3/helpers/address";
import {
  StyledAddress,
  StyledAddressTitle,
  StyledAddressesContainer,
  StyledBoldText,
  StyledButtonContainer,
  StyledMessage,
  StyledModalContainer,
  StyledSimpleModal,
  StyledInvalidZipCodeText,
  StyledZip,
} from "./index.styled";
import { logUnverifiedAddressSelected } from "@pepdirect/v3/helpers/analyticsLogger";

interface AddressVerificationModalProps {
  closeModal: () => void;
  enteredAddress: ShippingAddress;
  handleKeepEnteredAddress: () => void;
  handleSuggestedAddress: () => void;
  isOpen: boolean;
  suggestedAddress?: ShippingAddress | null;
  hasInvalidZip: boolean | undefined;
}

const CityAndStateWithZipError: FC<FormatCityStateZipByCountry> = ({
  city,
  countryFormat,
  state,
  zip,
}) => {
  const address = new PostalAddress();
  address
    .setCity(city || "")
    .setState(state || "")
    // for Canadian addresses
    .setProvince(state || "")
    // this creates a template string so that we can bold the zip below
    .setPostalCode(zip ? `\${zip}` : "")
    .setFormat({ country: countryFormat || "US" });

  return (
    <TemplateAsComponent
      template={address.toString()}
      map={{ zip: <StyledZip>{zip}</StyledZip> }}
    />
  );
};

interface DisplayedAddressProps {
  addr: ShippingAddress;
  title: string;
  hasInvalidZip?: boolean;
}

const DisplayedAddress: FC<DisplayedAddressProps> = ({
  addr,
  title,
  hasInvalidZip,
}) => {
  const { city, countryCode: countryFormat, line1, state, zip, line2 } = addr;
  const streetAddress = formatStreetByCountry({ line1, line2, countryFormat });
  const cityStateZip = formatCityStateZipByCountry({
    city,
    state,
    zip,
    countryFormat,
  });

  return (
    <StyledAddress>
      <StyledAddressTitle>{title}</StyledAddressTitle>
      <div>{streetAddress}</div>
      {hasInvalidZip ? (
        <CityAndStateWithZipError
          city={city}
          countryFormat={countryFormat}
          state={state}
          zip={zip}
        />
      ) : (
        <div>{cityStateZip}</div>
      )}
    </StyledAddress>
  );
};

export const AddressVerificationModal: FC<AddressVerificationModalProps> = ({
  closeModal,
  enteredAddress,
  handleKeepEnteredAddress,
  handleSuggestedAddress,
  isOpen,
  suggestedAddress,
  hasInvalidZip,
}) => {
  const { tenant } = useContext(TenantContext);
  const { localization } = useContext(LocalizationContext);

  const requireValidation =
    tenant?.contactConfig.shippingAddressConfig.requireValidation;
  const allowBypassAddressVerification =
    requireValidation === ValidationRequirement.Optional;

  const {
    addressErrorBoldText = "address",
    addressErrorText = "We were not able to verify the ${addressErrorBoldText} you provided.",
    editManuallyText = "Edit manually",
    editOnlyText = "Please edit your address to continue.",
    editOrSuggestionText = "Please use this suggested address or edit your address to continue.",
    enteredAddressText = "Entered address",
    invalidZipCodeText = "Invalid zip code.",
    keepEnteredButtonText = "Keep entered address",
    reviewAddressText = "Please review before continuing to payment.",
    suggestedAddressText = "Suggested address",
    titleText = "Address verification issue",
    useSuggestedButtonText = "Use suggested address",
    zipErrorBoldText = "zip code",
    zipErrorText = "We were not able to verify the ${zipErrorBoldText} you provided.",
  } = localization?.components?.addressVerification || {};

  const continueText = useMemo(() => {
    if (allowBypassAddressVerification && !hasInvalidZip) {
      return suggestedAddress ? editOrSuggestionText : reviewAddressText;
    }
    return suggestedAddress ? editOrSuggestionText : editOnlyText;
  }, [
    allowBypassAddressVerification,
    editOnlyText,
    editOrSuggestionText,
    hasInvalidZip,
    reviewAddressText,
    suggestedAddress,
  ]);
  const messageTemplate = hasInvalidZip ? zipErrorText : addressErrorText;
  const messageTemplateMap = hasInvalidZip
    ? {
        zipErrorBoldText: <StyledBoldText>{zipErrorBoldText}</StyledBoldText>,
      }
    : {
        addressErrorBoldText: (
          <StyledBoldText>{addressErrorBoldText}</StyledBoldText>
        ),
      };

  const keepEnteredAddress = () => {
    logUnverifiedAddressSelected();
    handleKeepEnteredAddress();
  };

  return (
    <StyledSimpleModal isOpen={isOpen}>
      <>
        <StyledModalContainer>
          <Heading level="h2" size="l">
            {titleText}
          </Heading>
          {hasInvalidZip && (
            <StyledInvalidZipCodeText>
              {invalidZipCodeText}
            </StyledInvalidZipCodeText>
          )}
          <StyledMessage>
            <TemplateAsComponent
              template={messageTemplate}
              map={messageTemplateMap}
              display="inline"
            />{" "}
            {continueText}
          </StyledMessage>

          <StyledAddressesContainer>
            <DisplayedAddress
              addr={enteredAddress}
              title={enteredAddressText}
              hasInvalidZip={hasInvalidZip}
            />

            {suggestedAddress && (
              <DisplayedAddress
                addr={suggestedAddress}
                title={suggestedAddressText}
              />
            )}
          </StyledAddressesContainer>
        </StyledModalContainer>

        <StyledButtonContainer>
          <Button
            onClick={closeModal}
            type={!suggestedAddress ? "primary" : "tertiary"}
          >
            {editManuallyText}
          </Button>
          {suggestedAddress && (
            <Button onClick={handleSuggestedAddress}>
              {useSuggestedButtonText}
            </Button>
          )}
          {allowBypassAddressVerification && !hasInvalidZip && (
            <Button onClick={keepEnteredAddress}>
              {keepEnteredButtonText}
            </Button>
          )}
        </StyledButtonContainer>
      </>
    </StyledSimpleModal>
  );
};
